import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import CachedIcon from '@mui/icons-material/Cached';
import { FixRuleDialog } from 'components/dialog/fixed_rule/Dialog';
import { buildSettingsBasePath, buildSettingsCrudPath, parseSettingsCrudRoute } from 'utils/settingsCrudRouting';

interface SubMenuProps {
    contextMenu: { mouseX: number; mouseY: number } | null;
    setContextMenu: (value: { mouseX: number; mouseY: number } | null) => void;
    selectedRow: any[];
    reloading: () => void;
    rows: any[];
    cleanSelected: () => void;
}

const ContextMenu: FC<SubMenuProps> = ({ contextMenu, setContextMenu, selectedRow, reloading, rows, cleanSelected }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const [localOpen, setLocalOpen] = useState(false);
    const [localMode, setLocalMode] = useState<'add' | 'edit' | 'delete' | 'info'>('add');
    const basePath = buildSettingsBasePath('fixed_rules');
    const modalRoute = useMemo(() => parseSettingsCrudRoute(location.pathname, 'fixed_rules'), [location.pathname]);
    const routeOpen = modalRoute.action === 'add' || (modalRoute.action === 'update' && modalRoute.id !== null) || (modalRoute.action === 'detail' && modalRoute.id !== null);
    const routeMode: 'add' | 'edit' | 'info' = modalRoute.action === 'add' ? 'add' : modalRoute.action === 'detail' ? 'info' : 'edit';
    const open = routeOpen || localOpen;
    const mode = routeOpen ? routeMode : localMode;

    const selectedRowFromRoute = useMemo(() => {
        if (modalRoute.id === null) return null;
        return rows.find((row) => Number(row.id) === modalRoute.id) || null;
    }, [rows, modalRoute.id]);
    const selectedRowsForDialog = selectedRowFromRoute ? [selectedRowFromRoute] : selectedRow;

    const closeDialog = () => {
        if (routeOpen) {
            navigate(basePath);
        } else {
            setLocalOpen(false);
        }
        cleanSelected();
    };

    const options = [
        {
            name: t('settings.fixed_rules.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSettingsCrudPath('fixed_rules', 'add'));
                setContextMenu(null);
            },
            color: '',
            selected: false,
            onlyOne: false,
            //(JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.CreateAccrualRule') ? false : true,
            permission: (JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.CreateFixedRule') ? true : false,
        },
        {
            name: t('settings.fixed_rules.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                const current = selectedRow[0];
                if (!current?.id) return;
                navigate(buildSettingsCrudPath('fixed_rules', 'update', current.id));
                setContextMenu(null);
            },
            color: '',
            disabled: selectedRow.length !== 1 ? true : false,
            permission:
                selectedRow.length === 1 && (JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.PatchFixedRule') ? true : false,
        },
        {
            name: t('settings.fixed_rules.table.context_menu.delete'),
            icon: <DeleteIcon color="error" />,
            click: () => {
                setLocalMode('delete');
                setLocalOpen(true);
                setContextMenu(null);
            },
            color: 'error',
            disabled: true, //selectedRow.length !== 1 ? true : false,
            selected: false,
            onlyOne: true,
            permission:
                selectedRow.length === 1 && (JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.DeleteFixedRule')
                    ? true
                    : false,
        },
        {
            name: t('settings.fixed_rules.table.context_menu.info'),
            icon: <InfoIcon />,
            disabled: selectedRow.length !== 1 ? true : false,
            click: () => {
                const current = selectedRow[0];
                if (!current?.id) return;
                navigate(buildSettingsCrudPath('fixed_rules', 'detail', current.id));
                setContextMenu(null);
            },
            color: '',
            permission:
                selectedRow.length === 1 && (JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.FixedRuleById') ? true : false,
        },
        {
            name: t('settings.fixed_rules.table.context_menu.reload'),
            icon: <CachedIcon />,
            color: '',
            click: () => {
                reloading();
                setContextMenu(null);
            },
            permission: true,
        },
    ];

    return (
        <>
            <Menu
                open={contextMenu ? true : false}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ boxShadow: 'none' }}
            >
                {options
                    .filter((option) => option.permission)
                    .map((option, index) => (
                        <MenuItem key={index} onClick={option.click} disabled={option.disabled}>
                            <ListItemIcon>{option.icon}</ListItemIcon>

                            <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                        </MenuItem>
                    ))}
            </Menu>

            <FixRuleDialog open={open} setOpen={setLocalOpen} mode={mode} rows={rows} selectedRow={selectedRowsForDialog} reloading={reloading} cleanSelected={closeDialog} />
        </>
    );
};

export { ContextMenu };
